/**
 * Role label utilities: apply org-specific labels to strings.
 * Replaces default "Athlete", "Coach", "Guardian" (and plurals) with org labels,
 * preserving capitalization of the matched token.
 */

import type { RoleLabelsByKey } from "../object-actions/types/types";
import { ROLE_CONFIG } from "../object-actions/types/types";

/** Default labels (Athlete, Coach, Guardian). parent is machine name. */
export const DEFAULT_ROLE_LABELS: RoleLabelsByKey = {
  athlete: "Athlete",
  coach: "Coach",
  parent: "Guardian",
};

/**
 * Preserve source capitalization when applying replacement.
 * e.g. "Athlete" -> "Patient", "ATHLETE" -> "PATIENT"
 */
function applyCasePreservingReplacement(match: string, replacement: string): string {
  if (match === match.toUpperCase()) return replacement.toUpperCase();
  if (match === match.toLowerCase()) return replacement.toLowerCase();
  if (match[0] === match[0]?.toUpperCase())
    return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
  return replacement;
}

function toPlural(word: string): string {
  return word.endsWith("s") ? word : word + "s";
}

/** Patterns and target key. Key is athlete | coach | parent. */
const REPLACEMENT_PAIRS: Array<{ pattern: RegExp; key: keyof RoleLabelsByKey; plural: boolean }> = [
  { pattern: /\bAthletes\b/g, key: "athlete", plural: true },
  { pattern: /\bathletes\b/g, key: "athlete", plural: true },
  { pattern: /\bATHLETES\b/g, key: "athlete", plural: true },
  { pattern: /\bAthlete\b/g, key: "athlete", plural: false },
  { pattern: /\bathlete\b/g, key: "athlete", plural: false },
  { pattern: /\bATHLETE\b/g, key: "athlete", plural: false },
  { pattern: /\bCoaches\b/g, key: "coach", plural: true },
  { pattern: /\bcoaches\b/g, key: "coach", plural: true },
  { pattern: /\bCOACHES\b/g, key: "coach", plural: true },
  { pattern: /\bCoach\b/g, key: "coach", plural: false },
  { pattern: /\bcoach\b/g, key: "coach", plural: false },
  { pattern: /\bCOACH\b/g, key: "coach", plural: false },
  { pattern: /\bGuardians\b/g, key: "parent", plural: true },
  { pattern: /\bguardians\b/g, key: "parent", plural: true },
  { pattern: /\bGuardian\b/g, key: "parent", plural: false },
  { pattern: /\bguardian\b/g, key: "parent", plural: false },
  { pattern: /\bParents\b/g, key: "parent", plural: true },
  { pattern: /\bparents\b/g, key: "parent", plural: true },
  { pattern: /\bParent\b/g, key: "parent", plural: false },
  { pattern: /\bparent\b/g, key: "parent", plural: false },
];

/**
 * Get display label for a role. Fallback to defaults when labels are null/undefined.
 */
export function getRoleDisplayLabel(
  labels: RoleLabelsByKey | null | undefined,
  roleKey: keyof RoleLabelsByKey,
  options?: { plural?: boolean }
): string {
  const resolved = labels ?? DEFAULT_ROLE_LABELS;
  let label = resolved[roleKey] ?? DEFAULT_ROLE_LABELS[roleKey];
  if (!label) label = DEFAULT_ROLE_LABELS[roleKey];
  if (options?.plural) label = toPlural(label);
  return label;
}

/**
 * Replace default role strings with org-specific labels, preserving capitalization.
 */
export function applyRoleLabels(
  text: string,
  labels?: RoleLabelsByKey | null | undefined
): string {
  const resolved = labels ?? DEFAULT_ROLE_LABELS;
  let result = text;
  for (const { pattern, key, plural } of REPLACEMENT_PAIRS) {
    const base = resolved[key];
    if (!base) continue;
    const replacement = plural ? toPlural(base) : base;
    result = result.replace(pattern, (match) =>
      applyCasePreservingReplacement(match, replacement)
    );
  }
  return result;
}

/** ROLE_CONFIG with all labels, descriptions, and features run through applyRoleLabels. */
export function applyRoleLabelsToRoleConfig(
  labels: RoleLabelsByKey | null | undefined
): typeof ROLE_CONFIG {
  const apply = (text: string) => applyRoleLabels(text, labels);
  const result: Record<string, { icon: string; label: string; color: string; description: string; features: string[] }> = {};
  for (const [key, val] of Object.entries(ROLE_CONFIG)) {
    result[key] = {
      ...val,
      label: apply(val.label),
      description: apply(val.description),
      features: val.features.map((f) => apply(f)),
    };
  }
  return result as unknown as typeof ROLE_CONFIG;
}
